import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import {
    ChevronDownIcon,
    BookOpenIcon,
    AcademicCapIcon,
    InformationCircleIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import MicroLearningList from './MicroLearningList';
import ProgressBar from '../../components/common/ProgressBar';

const CategoryCard = ({ category, catalogs }) => {
    const [showInfo, setShowInfo] = useState(false);

    return (
        <div className="card bg-base-200 shadow-lg ring-1 ring-base-content/5">
            <Disclosure>
                {({ open }) => (
                    <>
                        <div className="card-body p-4" 
                             ref={(element) => {
                                 if (open && element && window.innerWidth < 640) { // 640px ist der sm breakpoint
                                     const yOffset = -70;
                                     const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                     window.scrollTo({ top: y, behavior: 'smooth' });
                                 }
                             }}>
                            <DisclosureButton className="w-full">
                                <div className="grid grid-cols-12 gap-4 items-center">
                                    {/* Icon & Title Group */}
                                    <div className="col-span-12 sm:col-span-5">
                                        <div className="flex items-center gap-3">
                                            <div className="rounded-lg bg-primary/20 p-3 ring-1 ring-primary/30">
                                                <BookOpenIcon className="w-6 h-6 text-primary" />
                                            </div>
                                            <div className="flex-1 flex items-start">
                                                <div className="text-left">
                                                    <h2 className="text-lg font-bold text-base-content">
                                                        {category.name}
                                                    </h2>
                                                    <div className="text-xs text-base-content/70">
                                                        {category.total_questions} Fragen
                                                    </div>
                                                </div>
                                                <button
                                                    className="lg:hidden ml-2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowInfo(true);
                                                    }}
                                                >
                                                    <InformationCircleIcon className="w-5 h-5 text-primary" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress Bar & Chevron */}
                                    <div className="col-span-11 sm:col-span-6">
                                        <ProgressBar
                                            green={category.green_questions}
                                            yellow={category.yellow_questions}
                                            red={category.red_questions}
                                            white={category.white_questions}
                                            total={category.total_questions}
                                        />
                                    </div>

                                    {/* Chevron */}
                                    <div className="col-span-1 flex justify-end">
                                        <ChevronDownIcon
                                            className={`w-5 h-5 text-base-content/50 transition-all duration-300
                                                ${open ? 'transform rotate-180' : ''}`}
                                        />
                                    </div>
                                </div>
                            </DisclosureButton>

                            <DisclosurePanel>
                                <div className="mt-6 space-y-6">
                                    {/* Description and Wiki Link Section */}
                                    <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4">
                                        {/* Description Box */}
                                        <div className="hidden lg:block lg:col-span-3">
                                            <div className="bg-base-300/30 rounded-xl p-4 ring-1 ring-base-content/5">
                                                <div className="prose prose-sm max-w-none text-base-content/80">
                                                    {category.description}
                                                </div>
                                            </div>
                                        </div>
                                        {/* Wiki Link */}
                                        <div className="flex flex-col gap-2">
                                            <p className="text-base-content/80">
                                                Starte das Kapitel mit unserem{' '}
                                                <Link
                                                    to={`/wiki/${category.name.split(' ')[0].toLowerCase()}`}
                                                    className="text-primary hover:underline"
                                                >
                                                    Wiki-Artikel
                                                </Link>
                                            </p>
                                        </div>
                                    </div>

                                    {/* MicroLearningList */}
                                    <div className="pt-2">
                                        <MicroLearningList catalogs={catalogs} />
                                    </div>
                                </div>
                            </DisclosurePanel>
                        </div>
                    </>
                )}
            </Disclosure>

            {/* Mobile Info Modal */}
            {showInfo && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 lg:hidden">
                    <div className="bg-base-100 rounded-xl w-full max-w-lg relative">
                        <div className="flex justify-between items-start p-4 border-b border-base-content/10">
                            <h3 className="text-lg font-bold">{category.name}</h3>
                            <button
                                onClick={() => setShowInfo(false)}
                                className="btn btn-ghost btn-sm btn-circle"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 prose prose-sm max-w-none text-base-content/80">
                            {category.description}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoryCard;
